/**
 * @file PasswordField.tsx
 * @description Password input with a show/hide toggle. Mirrors FormField
 *   API (id, label, value, onChange, error) but adds a button on the
 *   trailing side that switches input type between "password" and "text".
 *   Useful for change-password / register / reset flows where users want
 *   to verify what they typed.
 * @module Components
 * @security Toggling to plaintext is a deliberate user action. The
 *   browser still controls clipboard/autofill; we never log the value.
 */

import type { InputHTMLAttributes, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type BaseProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

interface PasswordFieldProps extends BaseProps {
  id: string;
  label: string;
  error?: string;
}

export function PasswordField({
  id,
  label,
  error,
  className,
  ...rest
}: PasswordFieldProps): JSX.Element {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const describedBy = error !== undefined ? `${id}-error` : undefined;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-slate-700">
        {label}
      </label>
      <div className="relative">
        <input
          {...rest}
          id={id}
          type={show ? 'text' : 'password'}
          aria-invalid={error !== undefined}
          aria-describedby={describedBy}
          className={[
            'w-full px-3 py-2 ps-3 pe-10 text-sm',
            error !== undefined ? 'border-red-500' : '',
            className ?? '',
          ].join(' ')}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          aria-pressed={show}
          aria-label={show ? t('common.hide_password') : t('common.show_password')}
          title={show ? t('common.hide_password') : t('common.show_password')}
          className={[
            'absolute inset-y-0 end-0 px-3 flex items-center',
            'text-slate-500 hover:text-slate-800 transition-colors',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-md',
          ].join(' ')}
        >
          {show ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
      {error !== undefined ? (
        <p id={describedBy} className="text-xs text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function EyeIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 3l18 18" />
      <path d="M10.58 10.58A2 2 0 0 0 12 14a2 2 0 0 0 1.42-.58" />
      <path d="M9.88 5.09A10 10 0 0 1 12 5c6.5 0 10 7 10 7a17.2 17.2 0 0 1-3.16 4.19" />
      <path d="M6.61 6.61A17.6 17.6 0 0 0 2 12s3.5 7 10 7c1.74 0 3.31-.43 4.66-1.12" />
    </svg>
  );
}
